<script setup>
import { useGesture } from '@/composables/calendar/useGesture';
import { DEV_MODE } from '@/config/dev-mode';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';

import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { getTicketCountAPI, getAnalysisLogAPI } from '@/api/AnalysisApi';
import { useAnalysisServices } from '@/composables/saju-insight/useAnalysisServices';
import { formatConsoleMessage, isObjectEmpty, padNumber } from '@/composables/functions';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';
import { yearlyFortuneComponentMap } from './summaryComponentMap';

import overviewSajuSummary01 from '@/pages/mobile/analysis-saju-yearly-fortune/_summary_01.vue';
import overviewSajuSummary02 from '@/pages/mobile/analysis-saju-yearly-fortune/_summary_02.vue';
import SurveyCard from '@/components/calendar/drawer/pages/cards/SurveyCard.vue';

import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import PopAnalysisInfoOther from '@/components/saju-insight/common/PopAnalysisInfoOther.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import IconClose01 from '@/components/svg/IconClose01.vue';
import IconList from '@/components/svg/IconList.vue';
import { useSajuData } from '@/composables/saju-insight/useSajuData';
import AlertComponent from '@/components/common/Alert.vue';
import PopDesc from './_pop_desc.vue';
import {
  MOBILE_YEARLY_FORTUNE_CONTENTS,
  MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP,
} from './constants';
import { useRnBridge } from '@/composables/useRnBridge';
import { useRnMessage } from '@/composables/useRnMessage';

definePage({ meta: { layout: 'empty' } });

const { sendMessageToApp } = useRnBridge();

const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();
const analysisStore = useAnalysisStore();
const analysisDataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();
const mobileSajuLnbStore = useMobileSajuLnbStore();
const { serviceData, serviceContents, fetchContentByKey } = useAnalysisServices();

const { sajuData, yearlyFortuneData, yearlyFortuneSubData, baseCode, transdata } =
  storeToRefs(analysisDataStore);

// =============== 경고 팝업 ===============
const dialogAlert = ref(null);
const dialogTitle = ref('');
const dialogText = ref('');
const warningDetected = ref(false);
const warningMessage = ref('');

const showWarningAlert = (message) => {
  dialogTitle.value = message;
  dialogText.value = '';
  warningDetected.value = true;
  warningMessage.value = message;
  dialogAlert.value?.openAlertDialog();
};

// =============== 신년운세 컴포넌트별 데이터 슬라이스 ===============
const contents01Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
  전체요약: yearlyFortuneData.value?.['0_전체요약'],
}));

const contents02Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
  전체요약: yearlyFortuneData.value?.['0_전체요약'],
}));

const contents03Data = computed(() => ({
  전체요약: yearlyFortuneData.value?.['1_전체요약'],
  운세흐름: yearlyFortuneData.value?.['2_운세흐름'],
}));

const monthlyEnergyData = computed(() => ({
  전체요약: yearlyFortuneData.value?.['1_전체요약'],
}));

const contents04Data = computed(() => ({
  운세흐름: yearlyFortuneData.value?.['2_운세흐름'],
}));

const contents05Data = computed(() => ({
  운세해석: yearlyFortuneData.value?.['3_운세해석'],
}));

const contents06Data = computed(() => ({
  에너지방향: yearlyFortuneData.value?.['4_에너지방향'],
}));

const contents07Data = computed(() => ({
  에너지추천: yearlyFortuneData.value?.['5_에너지추천'],
  안정에너지: yearlyFortuneData.value?.['6_안정에너지'],
}));

const contents08Data = computed(() => ({
  종합전략: yearlyFortuneData.value?.['7_종합전략'],
}));

const guide02Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
}));

// =============== 웹뷰 통신 ===============
/** 모바일 사용자 정보 */
const guestUserId = ref(null);
const isInitializing = ref(true);

const debuggingHandler = ({ action, message }) => {
  if (DEV_MODE) {
    switch (action) {
      case 'debug':
        console.log(formatConsoleMessage(message));
        break;
      case 'error':
        console.error(formatConsoleMessage(message));
        break;
      default:
        console.warn(`Unknown debug action: ${action}`);
        break;
    }
  }

  sendMessageToApp({ action, message });
};

/** 모바일 앱과 통신 - useRnMessage 사용 */
const { accessToken, authUserId } = useRnMessage({
  onInit: async (payload, action) => {
    isInitializing.value = true;
    try {
      if (action === 'init') {
        mobileSajuLnbStore.setGuestUserId(payload?.guestUserId);
        mobileSajuLnbStore.setUserName(payload?.userName);
      }
      if (action === 'refresh') {
        if (guestUserId.value && !isObjectEmpty(sajuData.value)) {
          await refreshYearlyFortunePageData();
        }
      }
    } finally {
      isInitializing.value = false;
    }
  },
  onReset: () => resetPopup(),
});

// =============== PC 테스트 환경 ===============
const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];

if (userId.value) {
  // PC 버전에서 userId가 있으면 로그인 상태로 판단
  accessToken.value = true;
}

// =============== 사주 데이터 ===============
const { getSajuParams, formatSajuParams, getSajuData } = useSajuData();

const isLoadingSajuData = ref(false);
/** 사주 데이터 초기화 및 파생값 계산 */
const initializeSajuData = async () => {
  isLoadingSajuData.value = true;
  try {
    const sajuParams = await getSajuParams(guestUserId.value);
    if (!sajuParams) return;

    const formattedSajuParams = formatSajuParams(sajuParams, analysisDataStore.paramData);
    const sajuResult = await getSajuData(formattedSajuParams);
    if (!sajuResult) return;

    analysisStore.setUserName(sajuResult?.inputdata?.name);
    analysisDataStore.setSajuData(sajuResult);
  } catch (e) {
    debuggingHandler({ action: 'error', message: `상세분석 > 사주정보 요청 실패: ${e}` });
  } finally {
    isLoadingSajuData.value = false;
  }
};

// =============== 1. 이용중인 신년운세 정보 ===============

/** 권한에 따른 suggestionFlag: 권한 없음 = '1', 권한 있음 = '3' */
const suggestionFlag = computed(() => (serviceData.yearlyFortune.permission.value ? '3' : '1'));

/** 이용중인 신년운세 정보 요청 중 상태 */
const isLoadingAnalysisInfo = ref(false);

/** 이용중인 신년운세 정보 요청 완료 여부 */
const hasLoadedAnalysisInfo = ref(false);

/** 이용중인 신년운세 정보 요청 */
const getAnalysisInfo = async () => {
  isLoadingAnalysisInfo.value = true;
  hasLoadedAnalysisInfo.value = false;

  try {
    await fetchContentByKey('yearlyFortune');
    hasLoadedAnalysisInfo.value = true;
  } catch (e) {
    debuggingHandler({
      action: 'error',
      message: `신년운세 > 1. 이용중인 신년운세 정보 요청 실패: ${e}`,
    });
  } finally {
    isLoadingAnalysisInfo.value = false;
  }
};

// =============== 2. 신년운세 데이터 ===============
/** 신년운세 데이터 요청 중 상태 */
const isFetchingYearlyFortune = ref(false);

/** 신년운세 데이터 요청 완료 여부 */
const hasFetchedYearlyFortune = ref(false);

/** 신년운세 데이터 요청 params */
const yearlyFortuneParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestUserId.value,
    lang: 'KOR',
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 9,
    suggestionFlag: suggestionFlag.value,
    year: new Date().getFullYear(),
  };
});

/** 신년운세 기본 데이터 요청 */
const fetchSubData = async () => {
  hasFetchedYearlyFortune.value = false;
  isFetchingYearlyFortune.value = true;
  try {
    if (isObjectEmpty(analysisDataStore.yearlyFortuneSubData)) {
      const subDataParams = {
        ...yearlyFortuneParams.value,
        analysisTypeId: 8,
      };

      const result = await fetchBaseServiceAPI(subDataParams);

      // TODO: 데이터 구조에 맞게 수정 필요
      // if (result?.data?.연운종합분석) {
      //   analysisDataStore.setYearlyFortuneSubData(result?.data?.연운종합분석);
      //   analysisDataStore.setYearlyFortuneData({});

      //   hasFetchedYearlyFortune.value = true;
      // }
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `신년운세 > 2-1. 기본 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingYearlyFortune.value = false;
  }
};

/** 내 신년운세 데이터 요청 */
const fetchYearlyFortuneData = async () => {
  debuggingHandler({ action: 'debug', message: '=== fetchYearlyFortuneData 호출됨 ===' });
  hasFetchedYearlyFortune.value = false;
  isFetchingYearlyFortune.value = true;
  try {
    debuggingHandler({ action: 'debug', message: `yearlyFortuneData isEmpty: ${isObjectEmpty(analysisDataStore.yearlyFortuneData)}` });
    debuggingHandler({ action: 'debug', message: `yearlyFortuneParams: ${JSON.stringify(yearlyFortuneParams.value)}` });
    if (isObjectEmpty(analysisDataStore.yearlyFortuneData)) {
      debuggingHandler({ action: 'debug', message: `API 호출 시작 - suggestionFlag: ${yearlyFortuneParams.value?.suggestionFlag}` });
      const result = await fetchBaseServiceAPI(yearlyFortuneParams.value);
      debuggingHandler({ action: 'debug', message: `API result 전체: ${JSON.stringify(result)}` });

      const data = result.data || result?.rag_data;
      debuggingHandler({ action: 'debug', message: `data 존재: ${!!data}, keys: ${data ? Object.keys(data).join(',') : 'null'}` });
      if (data) {
        // 경고 응답 체크 (대운범위초과 등) - 데이터 저장하지 않고 경고 상태만 저장
        if (data['경고']) {
          debuggingHandler({ action: 'debug', message: `경고 감지: ${data['경고']['메시지']}` });
          warningDetected.value = true;
          warningMessage.value = data['경고']['메시지'] || '운세 분석이 어렵습니다.';
          analysisDataStore.setYearlyFortuneWarning(warningMessage.value);
          return;
        }
        analysisDataStore.setYearlyFortuneData(data);
        analysisDataStore.setYearlyFortuneSubData({});
        hasFetchedYearlyFortune.value = true;
        debuggingHandler({ action: 'debug', message: `저장 후 store 데이터 keys: ${Object.keys(analysisDataStore.yearlyFortuneData).join(',')}` });

        // 8_행운용어 → 용어해석 데이터 fetch
        const terms = data['8_행운용어'];
        if (Array.isArray(terms) && terms.length > 0) {
          await analysisDataStore.fetchSajuWords(terms.join(','));
        }
      } else {
        debuggingHandler({ action: 'debug', message: 'data가 null/undefined - 저장하지 않음' });
      }
    } else {
      debuggingHandler({ action: 'debug', message: '데이터가 이미 존재하여 API 호출 스킵' });
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `신년운세 > 2-2. 분석권 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingYearlyFortune.value = false;
    debuggingHandler({ action: 'debug', message: `=== fetchYearlyFortuneData 완료 === store 데이터 isEmpty: ${isObjectEmpty(analysisDataStore.yearlyFortuneData)}` });
  }
};

/** 신년운세 데이터 초기화 */
const initYearlyFortuneData = async () => {
  await fetchYearlyFortuneData();
};

/** 신년운세 페이지 데이터 로딩 상태 */
const isRefreshingPageData = ref(false);

/** 신년운세 페이지 데이터 새로고침 */
const refreshYearlyFortunePageData = async () => {
  isRefreshingPageData.value = true;
  try {
    // 각 API를 개별적으로 처리하여 일부 실패해도 페이지가 표시되도록 함
    await Promise.allSettled([
      getAnalysisInfo().catch((e) => {
        console.warn('신년운세 > getAnalysisInfo 실패 (페이지 표시에는 영향 없음):', e);
      }),
      analysisStore.getAnalysisLog(guestUserId.value, 9).catch((e) => { // 신년운세 typeId: 9
        console.warn('신년운세 > getAnalysisLog 실패 (페이지 표시에는 영향 없음):', e);
      }),
      paymentsStore.fetchTicketCount().catch((e) => {
        console.warn('신년운세 > fetchTicketCount 실패 (페이지 표시에는 영향 없음):', e);
      }),
      paymentsStore.getOwnPoint().catch((e) => {
        console.warn('신년운세 > getOwnPoint 실패 (페이지 표시에는 영향 없음):', e);
      }),
    ]);

    // 신년운세 데이터 초기화도 실패해도 페이지는 표시되도록 처리
    await initYearlyFortuneData().catch((e) => {
      console.warn('신년운세 > initYearlyFortuneData 실패 (페이지 표시에는 영향 없음):', e);
    });
  } catch (e) {
    // 예상치 못한 에러도 페이지 표시를 막지 않도록 처리
    console.warn('신년운세 > 페이지 새로고침 중 예상치 못한 에러 (페이지 표시에는 영향 없음):', e);
  } finally {
    // 항상 로딩 상태를 해제하여 페이지가 표시되도록 함
    isRefreshingPageData.value = false;
  }
};

// =============== 3. 컨텐츠 평가 ===============
/** 컨텐츠 평가 여부 */
const ratingStatus = ref(false);

/** 컨텐츠 평가 점수 */
const ratingResult = ref(0);

/** 컨텐츠 평가 조회하기 */
const getSajuYearlyFortuneRate = async () => {
  const objectKey = `${guestUserId.value}`;

  try {
    const params = {
      serviceName: 'YEARLY_FORTUNE',
      objectKey,
    };
    const result = await getDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      ratingResult.value = result.score;
    }
  } catch (e) {
    // 404는 평가 데이터가 없을 때 발생하는 정상적인 상황
    // 404 에러는 페이지 표시에 영향을 주지 않도록 조용히 처리
    if (e?.response?.status === 404 || e?.code === 'ERR_BAD_REQUEST') {
      // 404 에러는 백엔드 오류로만 콘솔에 표시 (페이지 표시에 영향 없음)
      console.warn('신년운세 > 평가 조회 404 (백엔드 오류): 평가 데이터가 없습니다.');
      ratingStatus.value = false;
      ratingResult.value = 0;
      // 404 에러는 정상적인 상황이므로 에러 핸들러에 전달하지 않음
      return;
    } else {
      // 404가 아닌 다른 에러도 안전하게 처리 (페이지 표시에 영향 없음)
      console.warn('신년운세 > 평가 조회 실패 (페이지 표시에는 영향 없음):', e?.message || e);
      ratingStatus.value = false;
      ratingResult.value = 0;
      // 평가 API 실패는 페이지 표시에 영향을 주지 않으므로 에러 핸들러에 전달하지 않음
    }
  }
};

/** 컨텐츠 평가하기 */
const postSajuYearlyFortuneRate = async (score) => {
  const objectKey = `${guestUserId.value}`;

  try {
    const params = {
      score,
      serviceName: 'YEARLY_FORTUNE',
      objectKey,
    };
    const result = await postDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      getSajuYearlyFortuneRate();
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `신년운세 > 3-2. 평가하기 실패: ${e}` });
  }
};

const showPopAnalysisInfoDo = async () => {
  console.log('=== showPopAnalysisInfoDo 함수 호출됨 ===');
  console.log('isRefreshingPageData.value:', isRefreshingPageData.value);
  console.log('serviceData.yearlyFortune.permission.value:', serviceData.yearlyFortune.permission.value);
  console.log('paymentsStore?.points:', paymentsStore?.points);
  console.log('serviceContents.yearlyFortune.value?.pointPrice:', serviceContents.yearlyFortune.value?.pointPrice);
  console.log('serviceData.yearlyFortune.ticketCount.value:', serviceData.yearlyFortune.ticketCount.value);

  if (isRefreshingPageData.value) {
    console.log('⚠️ isRefreshingPageData가 true여서 함수 종료');
    return;
  }

  // 신년운세권 사용중일 때,
  if (serviceData.yearlyFortune.permission.value) {
    console.log('✅ 신년운세권 사용중 - 팝업 닫기');
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    return;
  }

  // 팝업을 열기 전에 포인트 정보를 다시 가져오기
  await paymentsStore.getOwnPoint();
  console.log('포인트 정보 갱신 후:', paymentsStore?.points, paymentsStore?.formattedPoints);

  if (
    paymentsStore?.points < serviceContents.yearlyFortune.value?.pointPrice &&
    serviceData.yearlyFortune.ticketCount.value === 0
  ) {
    // 포인트가 적고, 분석권도 없을 때
    // 포인트 충전 팝업 표시 (분석권 구매 팝업 X)
    console.log('💰 포인트 충전 팝업 표시');
    showPopAnalysisInfoOther.value = false;
    showPopAnalysisInfo.value = true;
  } else {
    // 포인트 또는 분석권이 충분할 때,
    // 분석권 구매 팝업 표시 (포인트 충전 팝업 X)
    console.log('🎫 분석권 구매 팝업 표시');
    showPopAnalysisInfoOther.value = true;
    showPopAnalysisInfo.value = false;
  }
  console.log('=== showPopAnalysisInfoDo 함수 종료 ===');
};

watch(
  () => mobileSajuLnbStore.guestUserId,
  (newId) => {
    if (newId) guestUserId.value = newId;
  },
);

watch(guestUserId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    if (!isObjectEmpty(sajuData.value)) {
      await refreshYearlyFortunePageData();
      // 평가 데이터는 실패해도 페이지 표시에 영향 없도록 별도 처리
      getSajuYearlyFortuneRate().catch(() => {
        // 에러는 이미 함수 내부에서 처리됨
      });
    } else {
      await initializeSajuData();
      await refreshYearlyFortunePageData();
      // 평가 데이터는 실패해도 페이지 표시에 영향 없도록 별도 처리
      getSajuYearlyFortuneRate().catch(() => {
        // 에러는 이미 함수 내부에서 처리됨
      });
    }
  }
});

// =============== 하단 탭 영역 (용어해석) ===============
const showPopDesc = ref(false);
const openPopDesc = () => {
  if (isRefreshingPageData.value) return;
  // 경고 상태면 결제 팝업 대신 경고 알림 표시
  const storeWarning = analysisDataStore.yearlyFortuneWarning;
  if (warningDetected.value || storeWarning) {
    showWarningAlert(warningMessage.value || storeWarning);
    return;
  }
  if (!serviceData.yearlyFortune.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }
  showPopDesc.value = true;
};
const closePopDesc = () => {
  showPopDesc.value = false;
};

// =============== 페이지 이동 ===============
const currentPage = ref(0); // 현재 페이지

/** 진로 추천 데이터 개수 확인 (3개 이상이면 true, 미만이면 false) */
const showFullLayout = computed(() => {
  if (!pData.value?.진로학과?.일반추천분야) return true;
  const majorRecoListAll = Object.entries(pData.value.진로학과.일반추천분야);
  return majorRecoListAll.length >= 3;
});

/** 동적으로 전체 페이지 수 계산 */
const totalPages = computed(() => {
  // 신년운세는 가이드 2개 + 컨텐츠 7개 = 9페이지
  return Object.keys(yearlyFortuneComponentMap).length + 1;
});

const showSummary02 = ref(false); // overviewSajuSummary02 표시 여부

const showPopAnalysisInfo = ref(false);
const showPopAnalysisInfoOther = ref(false);

const goToCoverPage = () => {
  currentPage.value = 0;
  showSummary02.value = false;
};

const clickPrevPage = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
};

const clickNextPage = () => {
  console.log('=== clickNextPage 호출됨 ===');
  console.log('currentPage.value:', currentPage.value);
  console.log('serviceData.yearlyFortune.permission.value:', serviceData.yearlyFortune.permission.value);
  // 3페이지부터 데이터 필요 → 경고 상태면 진입 차단
  if (currentPage.value + 1 >= 3 && warningDetected.value) {
    showWarningAlert(warningMessage.value);
    return;
  }
  // 1~8페이지(Guide01~Contents02)는 무료, 9페이지(QuarterEnergy)부터 권한 필요
  if (currentPage.value === 8 && currentPage.value + 1 >= 9 && !serviceData.yearlyFortune.permission.value) {
    console.log('🔒 권한 없음 - showPopAnalysisInfoDo 호출');
    showPopAnalysisInfoDo();
    return;
  }
  if (currentPage.value >= totalPages.value) {
    console.log('📄 마지막 페이지 - 커버 페이지로 이동');
    goToCoverPage();
  } else {
    console.log('➡️ 다음 페이지로 이동');
    currentPage.value += 1;
  }
  console.log('=== clickNextPage 종료 ===');
};

/** 통합 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: clickNextPage, // 왼쪽 스와이프 → 다음 페이지 이동
  onSwipeRight: clickPrevPage, // 오른쪽 스와이프 → 이전 페이지 이동
  SWIPE_THRESHOLD: 150,
});

const handleNavigateToPage = async (id) => {
  console.log('=== handleNavigateToPage 호출됨 ===');
  console.log('id:', id);
  debuggingHandler({ action: 'debug', message: `신년운세 > 목차 이동: id=${id}` });

  // id를 사용하여 MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP에서 페이지 매핑
  const mappedPage = MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP[id];
  console.log('id:', id, 'mappedPage:', mappedPage);

  if (mappedPage === undefined) {
    console.error('❌ 매핑되지 않은 목차 ID:', id);
    debuggingHandler({
      action: 'error',
      message: `신년운세 > 매핑되지 않은 목차 ID: ${id}`,
    });
    return;
  }

  // 3페이지부터 데이터 필요 → 경고 상태면 진입 차단 (store도 함께 확인)
  const storeWarning = analysisDataStore.yearlyFortuneWarning;
  if (mappedPage >= 3 && (warningDetected.value || storeWarning)) {
    showWarningAlert(warningMessage.value || storeWarning);
    return;
  }
  // 데이터가 아직 없으면 fetch하여 경고 여부 먼저 확인
  if (mappedPage >= 3 && isObjectEmpty(analysisDataStore.yearlyFortuneData) && !warningDetected.value) {
    await fetchYearlyFortuneData();
    if (warningDetected.value || analysisDataStore.yearlyFortuneWarning) {
      showWarningAlert(warningMessage.value || analysisDataStore.yearlyFortuneWarning);
      return;
    }
  }
  // 1~8페이지(Guide01~Contents02)는 무료, 9페이지(QuarterEnergy)부터 권한 필요
  console.log('mappedPage >= 9:', mappedPage >= 9);
  console.log('!serviceData.yearlyFortune.permission.value:', !serviceData.yearlyFortune.permission.value);
  if (mappedPage >= 9 && !serviceData.yearlyFortune.permission.value) {
    console.log('🔒 권한 없음 - showPopAnalysisInfoDo 호출');
    showPopAnalysisInfoDo();
    return;
  }

  console.log('✅ 페이지 이동:', mappedPage);
  currentPage.value = mappedPage;
  console.log('=== handleNavigateToPage 종료 ===');
};

/** 팝업 닫기 */
const close = () => {
  if (window.ReactNativeWebView) {
    router.push('/mobile/saju-lnb');
  } else {
    router.back();
  }
};

const resetPopup = () => {
  currentPage.value = 0;
  showSummary02.value = false;
  showPopDesc.value = false;
};

const onClickRecharge = (shortageAmount) => {
  if (window.ReactNativeWebView) {
    sendMessageToApp({
      action: 'route',
      route: {
        screen: 'points',
        shortageAmount,
      },
    });
  }
};

const handelShowSummary02Open = () => {
  showSummary02.value = true;
};

// 페이지별 컴포넌트 반환 함수
const getComponentByPage = (page) => {
  return yearlyFortuneComponentMap[page] || null;
};

// 페이지별 CSS 클래스 반환 함수
const getComponentClass = (page) => {
  return page % 2 === 1 ? 'mobile-content-first' : 'mobile-content-second';
};

// 페이지별 데이터 반환 함수
const getDataByPage = (page) => {
  // 페이지 → 컴포넌트 매핑: 3-4: Guide02, 5-6: Contents01, 7-8: Contents02, ...
  if (page >= 3 && page <= 4) return guide02Data.value;
  if (page >= 5 && page <= 6) return contents01Data.value;
  if (page >= 7 && page <= 8) return contents02Data.value;
  if (page >= 9 && page <= 10) return contents03Data.value;
  if (page >= 11 && page <= 12) return monthlyEnergyData.value;
  if (page >= 13 && page <= 14) return contents04Data.value;
  if (page >= 15 && page <= 16) return contents05Data.value;
  if (page >= 17 && page <= 18) return contents06Data.value;
  if (page >= 19 && page <= 20) return contents07Data.value;
  if (page >= 21 && page <= 22) return contents08Data.value;
  return null;
};

const openFlag = ref('');
const openContentsList = () => {
  if (openFlag.value === '') openFlag.value = 'open';
  else openFlag.value = '';
};
const closeContentsList = () => (openFlag.value = '');

/** YEARLY_FORTUNE_CONTENTS의 id를 contentIdx로 변환 */
const getIdToContentIdx = (id) => {
  // id 1 → contentIdx 0 (가이드 1)
  // id 2 → contentIdx 1 (가이드 2, 빈 항목이지만 매핑은 필요)
  // id 3 → contentIdx 2 (컨텐츠 1)
  // id 4 → contentIdx 3 (컨텐츠 2)
  // ...
  if (id === 1) return 0;
  return id - 1;
};

/** index값으로 페이지 이동 */
const updateIndexNo = (id) => {
  debuggingHandler({ action: 'debug', message: `updateIndexNo id: ${id}` });
  console.log('=== updateIndexNo 호출됨 ===');
  console.log('목차 ID:', id);
  // id를 사용하여 MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP에서 페이지 매핑
  const mappedPage = MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP[id];
  console.log('id:', id, 'mappedPage:', mappedPage);

  if (mappedPage === undefined) {
    console.warn(`❌ 매핑되지 않은 목차 ID: ${id}`);
    return;
  }

  // 3페이지부터 데이터 필요 → 경고 상태면 진입 차단
  if (mappedPage >= 3 && warningDetected.value) {
    showWarningAlert(warningMessage.value);
    return;
  }

  // 1~8페이지(Guide01~Contents02)는 무료, 9페이지(QuarterEnergy)부터 권한 필요
  console.log('mappedPage >= 9:', mappedPage >= 9);
  console.log('!serviceData.yearlyFortune.permission.value:', !serviceData.yearlyFortune.permission.value);
  if (mappedPage >= 9 && !serviceData.yearlyFortune.permission.value) {
    console.log('🔒 권한 없음 - showPopAnalysisInfoDo 호출');
    showPopAnalysisInfoDo();
    return;
  }

  // 동적 페이지 수 범위 체크
  if (mappedPage > totalPages.value) {
    console.warn(`⚠️ 페이지 번호 ${mappedPage}가 전체 페이지 수 ${totalPages.value}를 초과합니다.`);
    return;
  }

  console.log('✅ 페이지 이동:', mappedPage);
  currentPage.value = mappedPage;
  closeContentsList();
  console.log('=== updateIndexNo 종료 ===');
};

// 인덱스
// const indexNo = ref(1)
watch(currentPage, (newPage) => {
  analysisStore.setYearlyFortuneIndex(newPage);
  // currentPage.value = newIndex
});

watch(
  () => analysisStore.selectedContentIndex,
  (newIdx) => {
    // newIdx는 contentIdx (0, 1, 2, ...)
    // YEARLY_FORTUNE_CONTENTS에서 contentIdx에 해당하는 항목 찾기
    // contentIdx 0 → id 1, contentIdx 1 → id 2, contentIdx 2 → id 3, ...
    if (newIdx !== null && MOBILE_YEARLY_FORTUNE_CONTENTS[newIdx]) {
      const target = MOBILE_YEARLY_FORTUNE_CONTENTS[newIdx];
      if (target?.id) updateIndexNo(target.id);
    }
  },
  { immediate: true },
);

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const handleChargePop = (shortageAmount) => {
  if (window.ReactNativeWebView) {
    onClickRecharge(shortageAmount);
  } else {
    const path = '/purchase/points';
    router.push(path);
  }
  showPopAnalysisInfo.value = false;
};

const handleClosePopOther = () => {
  showPopAnalysisInfoOther.value = false;
};

const handleChargePopOtherProduct = () => {
  if (window.ReactNativeWebView) {
    onClickRecharge();
  } else {
    router.push('/purchase/tickets');
  }
  showPopAnalysisInfoOther.value = false;
};

/** 신년운세 결제 성공 후 실행 */
const paymentSuccessDo = async (success) => {
  debuggingHandler({ action: 'debug', message: `=== paymentSuccessDo 호출됨 === success: ${success}` });
  if (success) {
    try {
      // 로딩 상태 먼저 활성화
      isRefreshingPageData.value = true;
      debuggingHandler({ action: 'debug', message: '1. isRefreshingPageData = true' });

      // 기존 데이터 클리어 (새 권한으로 새 데이터 요청을 위해)
      debuggingHandler({ action: 'debug', message: `2. 데이터 클리어 전 yearlyFortuneData 존재: ${!isObjectEmpty(analysisDataStore.yearlyFortuneData)}` });
      analysisDataStore.setYearlyFortuneData({});
      debuggingHandler({ action: 'debug', message: `3. 데이터 클리어 후 yearlyFortuneData 존재: ${!isObjectEmpty(analysisDataStore.yearlyFortuneData)}` });

      // 인덱스 초기화
      analysisStore.setYearlyFortuneIndex(1);
      analysisStore.setSelectedContentIndex(null);
      currentPage.value = 0;
      debuggingHandler({ action: 'debug', message: '4. 인덱스 초기화 완료' });

      debuggingHandler({ action: 'debug', message: `5. refreshYearlyFortunePageData 호출 전 - permission: ${serviceData.yearlyFortune.permission.value}, suggestionFlag: ${suggestionFlag.value}` });
      await refreshYearlyFortunePageData();
      debuggingHandler({ action: 'debug', message: `6. refreshYearlyFortunePageData 완료 - yearlyFortuneData 존재: ${!isObjectEmpty(analysisDataStore.yearlyFortuneData)}` });
      debuggingHandler({ action: 'debug', message: `6-1. yearlyFortuneData keys: ${Object.keys(analysisDataStore.yearlyFortuneData || {}).join(',')}` });
      debuggingHandler({ action: 'debug', message: `6-2. permission 재확인: ${serviceData.yearlyFortune.permission.value}, suggestionFlag: ${suggestionFlag.value}` });

      // 평가 데이터는 실패해도 페이지 표시에 영향 없도록 별도 처리
      getSajuYearlyFortuneRate().catch(() => {
        // 에러는 이미 함수 내부에서 처리됨
      });
      mobileSajuLnbStore.setNeedRefresh(true);
      debuggingHandler({ action: 'debug', message: '=== paymentSuccessDo 완료 ===' });
    } catch (e) {
      debuggingHandler({ action: 'error', message: `신년운세권 결제 후 새로고침 중 오류: ${e}` });
    }
  }
};

onMounted(async () => {
  // useRnMessage에서 이벤트 리스너 자동 등록

  //REVIEW: 테스트 중
  await nextTick();

  if (window.ReactNativeWebView) {
    if (mobileSajuLnbStore.guestUserId) {
      guestUserId.value = mobileSajuLnbStore.guestUserId;
    }
    // WebView 환경에서 메시지가 수신되지 않을 경우를 대비해 타임아웃 설정
    setTimeout(() => {
      if (isInitializing.value) {
        console.warn('신년운세 > 메시지가 수신되지 않아 초기화 상태를 해제합니다.');
        isInitializing.value = false;
      }
    }, 3000); // 3초 후에도 초기화가 완료되지 않으면 강제로 해제
  } else {
    //guestUserId.value = 24825(일반 44페이지)
    //guestUserId.value = 24681(분석 43페이지)
    //guestUserId.value = 25055(신년)
    guestUserId.value = 25076;
    // PC 테스트 환경에서는 초기화 완료로 설정
    isInitializing.value = false;
  }

  // guestUserId가 설정된 후 데이터 초기화
  // 페이지 표시를 막지 않도록 await하지 않고 백그라운드에서 로드
  if (guestUserId.value) {
    if (!isObjectEmpty(sajuData.value)) {
      // 페이지 표시를 막지 않도록 await 제거
      refreshYearlyFortunePageData().catch((e) => {
        console.warn(
          '신년운세 > refreshYearlyFortunePageData 실패 (페이지 표시에는 영향 없음):',
          e,
        );
      });
      // 평가 데이터는 실패해도 페이지 표시에 영향 없도록 별도 처리
      getSajuYearlyFortuneRate().catch(() => {
        // 에러는 이미 함수 내부에서 처리됨
      });
    } else {
      // 사주 데이터 초기화는 필요하지만, 실패해도 페이지는 표시되도록 처리
      initializeSajuData()
        .then(() => {
          // 사주 데이터 초기화 성공 후 페이지 데이터 로드
          refreshYearlyFortunePageData().catch((e) => {
            console.warn(
              '신년운세 > refreshYearlyFortunePageData 실패 (페이지 표시에는 영향 없음):',
              e,
            );
          });
        })
        .catch((e) => {
          console.warn('신년운세 > initializeSajuData 실패 (페이지 표시에는 영향 없음):', e);
          // 사주 데이터 초기화 실패해도 페이지 데이터는 로드 시도
          refreshYearlyFortunePageData().catch((e) => {
            console.warn(
              '신년운세 > refreshYearlyFortunePageData 실패 (페이지 표시에는 영향 없음):',
              e,
            );
          });
        });
      // 평가 데이터는 실패해도 페이지 표시에 영향 없도록 별도 처리
      getSajuYearlyFortuneRate().catch(() => {
        // 에러는 이미 함수 내부에서 처리됨
      });
    }
  }
});

onUnmounted(() => {
  // useRnMessage에서 이벤트 리스너 자동 해제
  analysisStore.setYearlyFortuneIndex(1);
  analysisStore.setSelectedContentIndex(null);
});
</script>

<template>
  <!-- 분석권 및 포인트 부족 -->
  <PopAnalysisInfo
    :isVisible="showPopAnalysisInfo"
    :analysisType="serviceContents.yearlyFortune.value"
    @close="handleClosePop"
    @charge="handleChargePop"
  />
  <!-- 분석권 또는 포인트 사용 가능 -->
  <PopAnalysisInfoOther
    :isVisible="showPopAnalysisInfoOther"
    :analysisType="serviceContents.yearlyFortune.value"
    :ticketCountData="serviceData.yearlyFortune.ticketData.value"
    :ticketCount="serviceData.yearlyFortune.ticketCount.value"
    @close="handleClosePopOther"
    @charge="handleChargePopOtherProduct"
    @payment-success="paymentSuccessDo"
  />

  <div
    class="pop-saju-reading-container mobile st-analysis-yearly-fortune"
    @mousedown="handleStart"
    @mousemove="handleMove"
    @mouseup="handleEnd"
    @touchstart="handleStart"
    @touchmove="handleMove"
    @touchend="handleEnd"
  >
    <AnalysisMobileHeader title="신년운세" :guestUserId="guestUserId" @close="close" />
    <div v-if="currentPage === 0" class="pop-ana-saju-reading-inner intro">
      <overviewSajuSummary01
        v-if="currentPage === 0 && !showSummary02"
        :guestUserId="guestUserId"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :getAnalysisLogDataList="serviceData.yearlyFortune.logData.value"
        :hasPermission="serviceData.yearlyFortune.permission.value"
        :ticketCountData="serviceData.yearlyFortune.ticketData.value"
        :yearlyFortuneLogData="serviceData.yearlyFortune.logData.value"
        @handleNavigateToPage="handleNavigateToPage"
        @handelShowSummary02Open="handelShowSummary02Open"
        @recharge="onClickRecharge"
      />
      <AnalysisLoadingOverlay
        :loading="currentPage === 0 && showSummary02 && (isInitializing || isLoadingSajuData || isFetchingYearlyFortune)"
        :message="'신년운세 분석중입니다!'"
      />
      <overviewSajuSummary02
        v-if="currentPage === 0 && showSummary02"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :hasPermission="serviceData.yearlyFortune.permission.value"
        :initializing="isInitializing || isLoadingSajuData"
        @navigateToPage="handleNavigateToPage"
        @updateShowSummary02="
          (value) => {
            console.log('updateShowSummary02 received:', value);
            showSummary02 = value;
          }
        "
        @goPopAnalysis="showPopAnalysisInfoDo"
        @recharge="onClickRecharge"
      />
    </div>

    <div v-if="currentPage !== 0" class="pop-ana-saju-reading-inner st-page st-content-page">
      <div v-if="currentPage >= 1 && currentPage <= totalPages" class="pop-body mobile">
        <component
          :is="getComponentByPage(currentPage)"
          :class="getComponentClass(currentPage)"
          :isMobile="true"
          :data="getDataByPage(currentPage)"
          :userName="mobileSajuLnbStore?.userName"
        />
      </div>

      <div v-if="currentPage === totalPages" class="pop-body mobile rating">
        <div class="sr__no-content-page">
          <SurveyCard
            style="width: 100%"
            :ratingResult="ratingResult"
            :ratingStatus="ratingStatus"
            @postRate="postSajuYearlyFortuneRate"
          />
        </div>
      </div>

      <div class="contentsListBtnBox mobile">
        <div class="list-box" :class="openFlag">
          <ul class="list-li-box">
            <li
              class="li mobile"
              v-for="item in MOBILE_YEARLY_FORTUNE_CONTENTS.filter(
                (content) => content.title !== '',
              )"
              :key="item.id"
              @click="updateIndexNo(item.id)"
            >
              {{ item.title }}
            </li>
          </ul>
        </div>
      </div>
      <div class="pop-footer-page-box">
        <div class="pop-desc-box">
          <button class="btn-desc" @click="openPopDesc">📖 용어해석</button>
        </div>
        <div class="pop-pagination">
          <IconList
            @click="openContentsList('open')"
            v-if="openFlag === ''"
            style="margin-top: 3px"
          />
          <IconClose01
            @click="openContentsList('')"
            v-if="openFlag === 'open'"
            style="margin-top: 3px"
          />
          <button class="pop-pagination__prev" @click="clickPrevPage">
            <i class="icon-chevron"></i>
          </button>
          <div class="pop-pagination__index">{{ padNumber(currentPage) }} / {{ totalPages }}</div>
          <button class="pop-pagination__next" @click="clickNextPage">
            <i class="icon-chevron right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 용어해석 팝업 -->
  <PopDesc v-model="showPopDesc" :guestUserId="guestUserId" @close="closePopDesc" />

  <!-- 경고 팝업 (대운범위초과 등) -->
  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
  />
</template>

<style lang="scss">
@import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common-page.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';

.pop-ana-saju-reading-inner {
  height: calc(100vh - 52px);
  overflow-y: unset;
  &.intro {
    overflow-y: auto;
  }
  &.st-page {
    height: calc(100vh - 104px);
  }
  .btn-footer-box {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    .btn-white {
      display: flex;
      width: 100%;
      height: 48px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 10px 0px rgba(0, 0, 0, 0.05);
      // color: var(--Gray-800, #333);
      color: $analysis-personality;
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }
  }
  .pop-body {
    height: auto;
    padding: 0px;
    .v-row {
      margin: 0px !important;
    }
    .personality-wrapper {
      padding: 24px 16px;
    }
    &.rating {
      height: 100%;
    }
  }
}
.st-analysis-yearly-fortune {
  .pop-ana-saju-reading-inner,
  .overview-cover {
    /* PC 신년운세 목차 배경색과 동일 */
    background: #fdf9f3;
  }
  /* 신년운세 모바일 첫화면 상단 이름 텍스트 스타일 */
  .overview-cover__top {
    color: #4a3b32;
    font-family: Pretendard;
    font-size: 32px;
    font-style: normal;
    font-weight: 400;
    line-height: 44px; /* 137.5% */
  }
  /* 신년운세 이용하기 버튼 스타일 */
  .btn-footer-box {
    .btn-white {
      color: #be3331 !important; /* 신년운세 컬러 */
      border-color: #be3331 !important;
    }
  }
}
.st-analysis-yearly-fortune .pop-ana-saju-reading-inner .pop-body {
  background: #fff;
}

.st-analysis-yearly-fortune .pop-ana-saju-reading-inner.st-content-page {
  background-color: #fff !important;
  // padding-bottom: 60px !important;
  padding-bottom: 0px;
  overflow-x: hidden !important;
}

/* 모바일에서 좌우 컬럼 분리 */
.mobile-content-first {
  .mobile-second {
    display: none !important;
  }
  .mobile-first {
    display: block !important;
  }
}

.mobile-content-second {
  .mobile-first {
    display: none !important;
  }
  .mobile-second {
    display: block !important;
  }
}

/* 용어해석 버튼 및 목차 스타일 - 50%씩 반반, 높이 동일 */
.st-analysis-yearly-fortune {
  .pop-footer-page-box {
    .pop-desc-box {
      width: 50% !important;
      height: 52px;
      .btn-desc {
        border-radius: 0;
      }
    }
    .pop-pagination {
      width: 50% !important;
      flex: none !important;
      height: 52px;
    }
  }
}
</style>
